// Import model
import { RemoteFileFieldOptions } from '../models/RemoteFileFieldOptions';

// Import base class serializer
import { ReferenceFieldOptionsSerializer } from './ReferenceFieldOptionsSerializer';

// Import dependency types (models and enums used in properties)
import { Condition } from '../models/Condition';
import { FieldOptions } from '../models/FieldOptions';
import { FormAction } from '../models/FormAction';
import { SelectFieldOptions } from '../models/SelectFieldOptions';
import { UploadFileOptions } from '../models/UploadFileOptions';

// Import dependency serializers (for complex types)
import { ConditionSerializer } from './ConditionSerializer';
import { FieldOptionsSerializer } from './FieldOptionsSerializer';
import { FormActionSerializer } from './FormActionSerializer';
import { SelectFieldOptionsSerializer } from './SelectFieldOptionsSerializer';
import { UploadFileOptionsSerializer } from './UploadFileOptionsSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for RemoteFileFieldOptions - handles JSON serialization and deserialization with type validation
 */
export class RemoteFileFieldOptionsSerializer {
  /**
   * Deserializes a JSON string into a RemoteFileFieldOptions instance
   */
  static deserialize(json: string): RemoteFileFieldOptions {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a RemoteFileFieldOptions instance
   */
  static fromObject(obj: any): RemoteFileFieldOptions {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to RemoteFileFieldOptions');
    }


    const instance: RemoteFileFieldOptions = {
      t: obj['_t'] as string,
      linkUrl: obj['LinkUrl'] as string | undefined,
      linkLabel: obj['LinkLabel'] as string | undefined,
      items: obj['Items'] as string | undefined,
      allowUnknown: obj['AllowUnknown'] as boolean | undefined,
      objectType: obj['ObjectType'] as string | undefined,
      objectTypeName: obj['ObjectType|Name'] as string | undefined,
      autoComplete: obj['AutoComplete'] as boolean | undefined,
      foreignFieldName: obj['ForeignFieldName'] as string | undefined,
      criteria: obj['Criteria'] ? (obj['Criteria'] as any[]).map((item: any) =>
        ConditionSerializer.fromObject(item)
      ) : [] as any,
      joinBehavior: obj['JoinBehavior'] as string | undefined,
      actions: obj['Actions'] ? (obj['Actions'] as any[]).map((item: any) =>
        FormActionSerializer.fromObject(item)
      ) : [] as any,
      contributeUserEvents: obj['ContributeUserEvents'] as string | undefined,
      valueType: obj['ValueType'] as string | undefined,
      uploadFileOptions: obj['UploadFileOptions'] ? UploadFileOptionsSerializer.fromObject(obj['UploadFileOptions']) : undefined as any,
      contentTypes: obj['ContentTypes'] ? (obj['ContentTypes'] as any[]).map((item: any) =>
        item as string
      ) : [],
      url: obj['Url'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of RemoteFileFieldOptions instances
   */
  static deserializeArray(json: string): RemoteFileFieldOptions[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a RemoteFileFieldOptions instance to a JSON string
   */
  static serialize(instance: RemoteFileFieldOptions): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a RemoteFileFieldOptions instance to a plain object
   */
  static toObject(instance: RemoteFileFieldOptions): any {
    return {
      '_t': instance.t,
      'LinkUrl': instance.linkUrl,
      'LinkLabel': instance.linkLabel,
      'Items': instance.items,
      'AllowUnknown': instance.allowUnknown,
      'ObjectType': instance.objectType,
      'ObjectType|Name': instance.objectTypeName,
      'AutoComplete': instance.autoComplete,
      'ForeignFieldName': instance.foreignFieldName,
      'Criteria': instance.criteria?.map((item: any) =>
        ConditionSerializer.toObject(item)
      ),
      'JoinBehavior': instance.joinBehavior,
      'Actions': instance.actions?.map((item: any) =>
        FormActionSerializer.toObject(item)
      ),
      'ContributeUserEvents': instance.contributeUserEvents,
      'ValueType': instance.valueType,
      'UploadFileOptions': instance.uploadFileOptions ? UploadFileOptionsSerializer.toObject(instance.uploadFileOptions) : undefined,
      'ContentTypes': instance.contentTypes?.map((item: any) =>
        item
      ),
      'Url': instance.url,
    };
  }

  /**
   * Serializes an array of RemoteFileFieldOptions instances to a JSON string
   */
  static serializeArray(instances: RemoteFileFieldOptions[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
